import React, { useState } from "react";
import PropTypes from "prop-types";
import { useFollowersList } from "../../hooks/user/useFollowersList";
import { useFollowingList } from "../../hooks/user/useFollowingList";
import UserCard from "./UserCard";
import Button from "../common/Button";
import { FollowersListSkeleton } from "../common/SkeletonLoader";

/**
 * FollowersList component for displaying paginated lists of followers or following
 *
 * Features:
 * - Paginated list display (20 users per page)
 * - User cards with profile pictures, usernames, and follow buttons
 * - Empty states with encouraging messages
 * - Loading states and error handling
 * - Responsive design for different screen sizes
 *
 * Requirements: 4.1, 4.2, 4.3, 4.4, 4.5, 4.6
 */
const FollowersList = ({
  userId,
  type = "followers",
  className = "",
  compact = false,
  onUserClick,
  searchQuery = "",
  ...rest
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const limit = 20;

  // Use appropriate hook based on type
  const useListHook =
    type === "followers" ? useFollowersList : useFollowingList;
  const { data, isLoading, isError, error, isFetching, refetch } = useListHook(
    userId,
    currentPage,
    limit
  );

  // Handle search functionality
  React.useEffect(() => {
    if (data?.data?.users || data?.users) {
      const users = data?.data?.users || data?.users || [];

      if (searchQuery.trim()) {
        const filtered = users.filter((user) => {
          const username = (user.username || "").toLowerCase();
          const name = (user.name || "").toLowerCase();
          const email = (user.email || "").toLowerCase();
          const query = searchQuery.toLowerCase();

          return (
            username.includes(query) ||
            name.includes(query) ||
            email.includes(query)
          );
        });
        setFilteredUsers(filtered);
      } else {
        setFilteredUsers(users);
      }
    } else {
      setFilteredUsers([]);
    }
  }, [data, searchQuery]);

  // Reset to first page when search query changes
  React.useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  // Handle page navigation
  const handleNextPage = () => {
    if (data?.pagination?.hasNext) {
      setCurrentPage((prev) => prev + 1);
    }
  };

  const handlePrevPage = () => {
    if (data?.pagination?.hasPrev) {
      setCurrentPage((prev) => prev - 1);
    }
  };

  const handlePageClick = (page) => {
    setCurrentPage(page);
  };

  // Generate page numbers for pagination
  const generatePageNumbers = () => {
    if (!data?.pagination) return [];

    const { page, totalPages } = data.pagination;
    const pages = [];
    const maxVisiblePages = 5;

    let startPage = Math.max(1, page - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    // Adjust start page if we're near the end
    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    return pages;
  };

  // Loading state - use skeleton loader for better UX
  if (isLoading) {
    return (
      <div className={`space-y-4 ${className}`} {...rest}>
        <div className="mb-4">
          <h3 className="text-lg font-semibold text-dark-grey mb-2">
            {type === "followers" ? "Followers" : "Following"}
          </h3>
          <p className="text-medium-grey text-sm">
            Loading {type === "followers" ? "followers" : "following"}...
          </p>
        </div>
        <FollowersListSkeleton count={5} compact={compact} />
      </div>
    );
  }

  // Error state with enhanced error handling
  if (isError) {
    const getErrorMessage = () => {
      if (error?.response?.status === 404) {
        return "User not found or their profile is not accessible.";
      }
      if (error?.response?.status === 403) {
        return "You don't have permission to view this user's followers.";
      }
      if (error?.response?.status >= 500) {
        return "Server error. Our team has been notified and is working on a fix.";
      }
      if (error?.code === "ECONNABORTED") {
        return "Request timed out. Please check your connection and try again.";
      }
      if (error?.request && !error?.response) {
        return "Network error. Please check your internet connection.";
      }
      return error?.message || "Something went wrong while loading the list.";
    };

    return (
      <div className={`text-center py-8 ${className}`} {...rest}>
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 max-w-md mx-auto">
          <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg
              className="w-6 h-6 text-red-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
              />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-red-800 mb-2">
            Unable to load {type === "followers" ? "followers" : "following"}
          </h3>
          <p className="text-red-600 mb-4 text-sm leading-relaxed">
            {getErrorMessage()}
          </p>
          <div className="flex gap-2 justify-center">
            <Button
              onClick={() => refetch()}
              variant="primary"
              size="sm"
              disabled={isFetching}
            >
              {isFetching ? "Retrying..." : "Try Again"}
            </Button>
            <Button
              onClick={() => window.location.reload()}
              variant="secondary"
              size="sm"
            >
              Refresh Page
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Handle nested data structure from backend
  const users = searchQuery.trim()
    ? filteredUsers
    : data?.data?.users || data?.users || [];
  const pagination = data?.data?.pagination || data?.pagination || {};

  // For search results, create custom pagination
  const searchPagination = searchQuery.trim()
    ? {
        page: 1,
        totalPages: 1,
        total: filteredUsers.length,
        hasNext: false,
        hasPrev: false,
        limit: filteredUsers.length,
      }
    : pagination;

  // Empty state
  if (users.length === 0) {
    // Different messages for search vs no data
    if (searchQuery.trim()) {
      return (
        <div className={`text-center py-12 ${className}`} {...rest}>
          <div className="bg-light-grey/20 rounded-lg p-8">
            <div className="w-16 h-16 bg-light-grey rounded-full flex items-center justify-center mx-auto mb-4">
              <svg
                className="w-8 h-8 text-medium-grey"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-dark-grey mb-2">
              No results found
            </h3>
            <p className="text-medium-grey mb-3">
              No {type} found matching "{searchQuery}"
            </p>
            <p className="text-sm text-primary font-medium">
              Try adjusting your search terms
            </p>
          </div>
        </div>
      );
    }

    const emptyMessages = {
      followers: {
        title: "No followers yet",
        message: "When people follow this user, they'll appear here.",
        encouragement: "Be the first to follow and show your support!",
      },
      following: {
        title: "Not following anyone yet",
        message: "When this user follows others, they'll appear here.",
        encouragement: "Discover amazing users to follow in the community!",
      },
    };

    const emptyContent = emptyMessages[type];

    return (
      <div className={`text-center py-12 ${className}`} {...rest}>
        <div className="bg-light-grey/20 rounded-lg p-8">
          <div className="w-16 h-16 bg-light-grey rounded-full flex items-center justify-center mx-auto mb-4">
            <svg
              className="w-8 h-8 text-medium-grey"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
              />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-dark-grey mb-2">
            {emptyContent.title}
          </h3>
          <p className="text-medium-grey mb-3">{emptyContent.message}</p>
          <p className="text-sm text-primary font-medium">
            {emptyContent.encouragement}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={`space-y-6 ${className}`} {...rest}>
      {/* Users List */}
      <div className="space-y-3">
        {users.map((user) => {
          const userCardProps = {
            key: user._id || user.id,
            user: user,
            compact: compact,
            showFollowButton: true,
            className: "hover:bg-light-grey/10 transition-colors duration-200",
          };

          // Only pass onClick if it's actually a function
          if (onUserClick && typeof onUserClick === "function") {
            userCardProps.onClick = onUserClick;
          }

          return <UserCard {...userCardProps} />;
        })}
      </div>

      {/* Loading indicator for pagination */}
      {isFetching && (
        <div className="text-center py-4">
          <div className="inline-flex items-center gap-2 text-medium-grey">
            <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
            <span className="text-sm">Loading...</span>
          </div>
        </div>
      )}

      {/* Search Results Info */}
      {searchQuery.trim() && (
        <div className="pt-4 border-t border-light-grey">
          <div className="text-sm text-medium-grey text-center">
            {filteredUsers.length > 0
              ? `Found ${filteredUsers.length} ${type} matching "${searchQuery}"`
              : `No ${type} found matching "${searchQuery}"`}
          </div>
        </div>
      )}

      {/* Pagination Controls - only show when not searching */}
      {!searchQuery.trim() && searchPagination.totalPages > 1 && (
        <div className="flex items-center justify-between pt-6 border-t border-light-grey">
          {/* Page Info */}
          <div className="text-sm text-medium-grey">
            Showing {(searchPagination.page - 1) * searchPagination.limit + 1}{" "}
            to{" "}
            {Math.min(
              searchPagination.page * searchPagination.limit,
              searchPagination.total
            )}{" "}
            of {searchPagination.total}{" "}
            {type === "followers" ? "followers" : "following"}
          </div>

          {/* Pagination Buttons */}
          <div className="flex items-center gap-2">
            {/* Previous Button */}
            <Button
              onClick={handlePrevPage}
              disabled={!searchPagination.hasPrev || isFetching}
              variant="secondary"
              size="sm"
              className="px-3 py-1"
            >
              Previous
            </Button>

            {/* Page Numbers */}
            <div className="hidden sm:flex items-center gap-1">
              {generatePageNumbers().map((pageNum) => (
                <button
                  key={pageNum}
                  onClick={() => handlePageClick(pageNum)}
                  disabled={isFetching}
                  className={`
                    px-3 py-1 text-sm rounded-md transition-colors duration-200
                    ${
                      pageNum === searchPagination.page
                        ? "bg-primary text-white"
                        : "text-medium-grey hover:bg-light-grey/50"
                    }
                    ${isFetching ? "opacity-50 cursor-not-allowed" : ""}
                  `}
                >
                  {pageNum}
                </button>
              ))}
            </div>

            {/* Next Button */}
            <Button
              onClick={handleNextPage}
              disabled={!searchPagination.hasNext || isFetching}
              variant="secondary"
              size="sm"
              className="px-3 py-1"
            >
              Next
            </Button>
          </div>
        </div>
      )}

      {/* Mobile Page Info - only show when not searching */}
      {!searchQuery.trim() && (
        <div className="sm:hidden text-center text-sm text-medium-grey">
          Page {searchPagination.page} of {searchPagination.totalPages}
        </div>
      )}
    </div>
  );
};

FollowersList.propTypes = {
  userId: PropTypes.string.isRequired,
  type: PropTypes.oneOf(["followers", "following"]).isRequired,
  className: PropTypes.string,
  compact: PropTypes.bool,
  onUserClick: PropTypes.func,
  searchQuery: PropTypes.string,
};

export default FollowersList;
